import Vue from 'vue'
import session from '@/shared/session'
import downloadjs from 'downloadjs'

// initial state
const initialState = () => ({
	current: null,
	relations: [],
	memo: null,
	medicalcards: [],
	decursus: [],
	episodes:[],
	logs: [],
	exports: [],
	current_consult: null,

	physicians: []
})

const state = initialState()

// getters
const getters = {
	getPatientCurrentConsult: (state) => () => {
		return state.current_consult
	},
	getPatientDemographics: (state) => () => {
		return state.current
	},
	getRelations: (state) => () => {
		return state.relations
	},
	getPatientMemo: (state) => () => {
		return state.memo
	},
	getPatientMedicalCards: (state) => () => {
		return state.medicalcards
	},
	getPatientEpisodes: (state) => () => {
		return state.episodes
	},
	getPatientDecursus: (state) => () => {
		return state.decursus
	},
	getPatientLogs: (state) => () => {
		return state.logs
	},
	getPatientExports: (state) => () => {
		return state.exports
	},
}

// actions
const actions = {
	resetState ({ commit }) {
		commit('reset_state')
	},

	async downloadErrorImportBatch({ commit }, uuid) {
		const download = await session
			.get(`/api/patients/importer/${uuid}/download/`)
		
		const { content, filename, mime_type } = download.data
    downloadjs(content, filename, mime_type)

     return true
	},

	async clearImportData({ commit }, uuid) {
		await session
			.delete(`/api/patients/importer/${uuid}/clear/`)
	},
	
  async importBatch({ commit }, formData) {
    const batch = await session.post('/api/patients/importer/import_batch/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return batch.data
  },

	async getCurrentImport({ commit }, data) {
		const batch = await session
			.get(`/api/patients/importer/current_import_batch/`, {
				params: data
			})

		return batch.data
	},

	async getPhysicians({ commit, state }) {
		const physicians = await session.get('/api/patients/physicians/')
		commit("set_physicians", physicians.data)
		return state.physicians
	},

	async filterRelatedPatients({ commit, state }, params) {
		const patients = await session
			.get(`/api/patients/${state.current?.id}/related/`, {
				params: params,
			})
		return patients.data
	},

	async filterPatients({ commit }, params) {
		const patients = await session
			.get("/api/patients/", {
				params: params,
			})
		return patients.data
	},

	async filterEmployers({ commit, state }, params) {
		const items = await session
			.get('/api/catalogue/employers/', {
					params: params
			})

		return items.data
	},

	createEmployer({ commit }, data) {
		return session
				.post(`/api/catalogue/employers/`, data)
				.then((employer) => {
					return employer.data
				})
	},

	deleteEmployer({ commit, dispatch }, id) {
		return session
			.delete(`/api/catalogue/employers/${id}/`)
	},

	async getRelationsForCurrentPatient({ commit, state, dispatch }) {
		if (!state.current) {
			return
		}

		dispatch("loading/load", "relations", { root: true })

		const relations = await session
			.get(`/api/patients/${state.current?.id}/relations/`)

		commit("set_current_data", {key: 'relations', value: relations.data})

		dispatch("loading/end", "relations", { root: true })

		return state.relations
	},

	async getMedicalCardsForCurrentPatient({ commit, state, dispatch }) {
		if (!state.current) {
			return
		}

		dispatch("loading/load", "medicalcards", { root: true })

		const medicalcards = await session
			.get(`/api/patients/${state.current?.id}/medicalcards/`)

		commit("set_current_data", {key: 'medicalcards', value: medicalcards.data})

		dispatch("loading/end", "medicalcards", { root: true })

		return state.medicalcards
	},

	async getMemoForCurrentPatient({ commit, state, dispatch }) {
		if (!state.current) {
			return
		}

		dispatch("loading/load", "memo", { root: true })

		const memo = await session
			.get(`/api/patients/${state.current?.id}/memo/`)

		commit("set_current_data", {key: 'memo', value: memo.data})

		dispatch("loading/end", "memo", { root: true })

		return state.memo
	},

	async getDecursusForCurrentPatient({ commit, state, dispatch }) {
		if (!state.current) {
			return
		}

		dispatch("loading/load", "patient_decursus", { root: true })

		const decursus = await session
			.get(`/api/patients/${state.current?.id}/decursus/`)

		commit("set_current_data", {key: 'decursus', value: decursus.data})

		dispatch("loading/end", "patient_decursus", { root: true })

		return state.decursus
	},

	async getEpisodesForCurrentPatient({ commit, state, dispatch }) {
		if (!state.current) {
			return
		}

		dispatch("loading/load", "patient_episodes", { root: true })

		const episodes = await session
			.get(`/api/patients/${state.current?.id}/episodes/`)

		commit("set_current_data", {key: 'episodes', value: episodes.data})

		dispatch("loading/end", "patient_episodes", { root: true })

		return state.episodes
	},

	async getLogTypesForCurrentPatient({ state, dispatch }) {
		if (!state.current) {
			return
		}

		dispatch("loading/load", "log_types", { root: true })

		const log_types = await session
			.get(`/api/patients/${state.current?.id}/log_types/`)

		dispatch("loading/end", "log_types", { root: true })

		return log_types.data
	},

	async getLogsForCurrentPatient({ commit, state, dispatch }, params) {
		if (!state.current) {
			return
		}

		dispatch("loading/load", "logs", { root: true })
		const logs = await session
			.get(`/api/patients/${state.current?.id}/logs/`, { params: params })

		commit("set_current_data", {key: 'logs', value: logs.data})
		dispatch("loading/end", "logs", { root: true })
		return state.logs
	},

	async getExportsForCurrentPatient({ commit, dispatch, state }) {
		if (!state.current) {
			return
		}

		dispatch("loading/load", "exports", { root: true })
		const exports = await session
			.get(`/api/patients/${state.current?.id}/exports/`)

		commit("set_current_data", {key: 'exports', value: exports.data})
		dispatch("loading/end", "exports", { root: true })
		return state.exports
	},

	async getRunningExportsForCurrentPatient({ commit, dispatch, state }) {
		if (!state.current) {
			return
		}

		const exports_running = await session
			.get(`/api/patients/${state.current?.id}/running_exports/`)

		return exports_running.data ? exports_running.data.running : false
	},

	async getSelectedPatientById({ commit, state, dispatch }, id) {
		const current = await session
			.get(`/api/patients/${id}/`)

			const insurances = await dispatch("insurances/getInsurancesBySelectedPatientId", id, { root: true })

			return [current.data, insurances]	
	},

	async getPatientById({ commit, state, dispatch }, id) {

		dispatch("loading/load", "patient", { root: true })

		const current = await session
			.get(`/api/patients/${id}/`)

		commit("set_current_patient", current.data)

		// const [demographics, relations, memo, medicalcards, logs, current_consult] = await Promise.all([
		// 	session.get(`/api/patients/${id}/`),
		// 	session.get(`/api/patients/${id}/relations/`),
		// 	session.get(`/api/patients/${id}/memo/`),
		// 	session.get(`/api/patients/${id}/medicalcards/`),
		// 	session.get(`/api/patients/${id}/logs/`),
		// 	session.get(`/api/patients/${id}/current_consult/`),
		// ])

		const insurances = await dispatch("insurances/getInsurancesByPatientId", id, { root: true })

		// commit("set_current", {key: 'id', value: id})
		// commit("set_current", {key: 'demographics', value: demographics.data})
		// commit("set_current", {key: 'relations', value: relations.data})
		// commit("set_current", {key: 'memo', value: memo.data})
		// commit("set_current", {key: 'medicalcards', value: medicalcards.data})
		// commit("set_current", {key: 'logs', value: logs.data})
		// commit("set_current", {key: 'current_consult', value: current_consult.data})

		dispatch("loading/end", "patient", { root: true })

		return [state.current, insurances]
	},

	async getNewPatientById({ commit, dispatch, state }, id) {
		dispatch("resetAll", null, { root: true })
		const patient = await dispatch("getPatientById", id)
		return patient
	},

	exportCurrentPatient({ commit, state, dispatch }, { type, episodes }) {
		dispatch("loading/load", "export_patient", { root: true })
		return session
				.post(`/api/patients/${state.current?.id}/export/`, {type: type, episodes: episodes})
				.then(() => {
					return dispatch("getExportsForCurrentPatient")
				})
	      .finally(() => {
	        dispatch("loading/end", "export_patient", { root: true })
	      })
	},

	createPatient({ commit, state, dispatch }, data) {
		dispatch("loading/load", "create_patient", { root: true })
		return session
				.post(`/api/patients/`, data)
				.then((demographics) => {
					commit("set_current_patient", demographics.data)

					return state.current
				})
	      .finally(() => {
	        dispatch("loading/end", "create_patient", { root: true })
	      })
	},

	updatePatient({ commit, state, dispatch }, data) {
		dispatch("loading/load", "update_patient", { root: true })
		return session
			.put(`/api/patients/${state.current?.id}/`, data)
			.then((demographics) => {
				commit("set_current_patient", demographics.data)

				return state.current
			})
      .finally(() => {
        dispatch("loading/end", "update_patient", { root: true })
      })
	},

	createPatientRelation({ commit, state, dispatch }, data) {
		dispatch("loading/load", "patient_relation", { root: true })
		return session
				.post(`/api/patients/relations/`, {...data, patient: state.current?.id})
				.then(() => {
					return dispatch("getRelationsForCurrentPatient")
				})
	      .finally(() => {
	        dispatch("loading/end", "patient_relation", { root: true })
	      })
	},

	updatePatientRelation({ commit, state, dispatch }, { id, data }) {
		dispatch("loading/load", "patient_relation", { root: true })
		return session
			.patch(`/api/patients/relations/${id}/`, data)
			.then(() => {
				return dispatch("getRelationsForCurrentPatient")
			})
      .finally(() => {
        dispatch("loading/end", "patient_relation", { root: true })
      })
	},
	
	deletePatientRelation({ commit, dispatch }, id) {
    	dispatch("loading/load", "patient_relation", { root: true })
		return session
			.delete(`/api/patients/relations/${id}/`)
			.then(() => {
				return dispatch("getRelationsForCurrentPatient")
			})
			.finally(() => {
				dispatch("loading/end", "patient_relation", { root: true })
			})
	},

	async getPatientLastConsultation({ commit }, id) {
		const last_consult  = await session
			.get(`/api/patients/${id}/last_consult/`)
		return last_consult.data
	},

	async getPatientCurrentConsultation({ commit }, id) {
		const current_consult  = await session
			.get(`/api/patients/${id}/current_consult/`)
		return current_consult.data
	},

	async getPatientFutureConsultations({ commit }, id) {
		const consultations  = await session
			.get(`/api/patients/${id}/future_consults/`)
		return consultations.data
	},

	async getPatientQRCode({ commit, state, dispatch }) {
		dispatch("loading/load", "portal", { root: true })
		const qrcode  = await session
			.get(`/api/patients/${state.current?.id}/qrcode/`)
		dispatch("loading/end", "portal", { root: true })	
		return qrcode.data
	},

	createPatientConsultation({ state, dispatch }, { id, data }) {
		dispatch("loading/load", "consultation", { root: true })
		return session
			.post(`/api/patients/${id}/consult/`, data)
			.then((consultation) => {
				return consultation.data
			})
			.finally(() => {
				dispatch("loading/end", "consultation", { root: true })
			})
	},

	createPatientMemo({ commit, state, dispatch }, data) {
		dispatch("loading/load", "memo", { root: true })
		return session
			.post(`/api/patients/memo/`, {...data, patient: state.current?.id})
			.then(memo => {
				return dispatch("getMemoForCurrentPatient")
			})
		.finally(() => {
				dispatch("loading/end", "memo", { root: true })
		})
	},

	updatePatientMemo({ commit, dispatch }, { id , data}) {
		dispatch("loading/load", "memo", { root: true })
		return session
				.put(`/api/patients/memo/${id}/`, data)
				.then(memo => {
					return dispatch("getMemoForCurrentPatient")
				})
		.finally(() => {
				dispatch("loading/end", "memo", { root: true })
		})
	},

	uploadMedicalCard({ commit, dispatch }, data) {
    dispatch("loading/load", "medicalcards", { root: true })
		return session
			.post(`/api/patients/medicalcards/`, data)
			.then(() => {
				return dispatch("getMedicalCardsForCurrentPatient")
			})
			.finally(() => {
				dispatch("loading/end", "medicalcards", { root: true })
			})
	},

	updateMedicalCard({ commit, dispatch }, {id, data}) {
		dispatch("loading/load", "update_medicalcard", { root: true })
		return session
		.patch(`/api/patients/medicalcards/${id}/`, data)
		.then((medicalcard) => {
			return dispatch("getMedicalCardsForCurrentPatient")
		})
    .finally(() => {
      dispatch("loading/end", "update_medicalcard", { root: true })
    })

	},

	deleteMedicalCard({ commit, dispatch }, id) {
    	dispatch("loading/load", "medicalcards", { root: true })
		return session
			.delete(`/api/patients/medicalcards/${id}/`)
			.then(() => {
				return dispatch("getMedicalCardsForCurrentPatient")
			})
			.finally(() => {
				dispatch("loading/end", "medicalcards", { root: true })
			})
	},

	updatePatientStatus({ commit, dispatch }, status) {
		dispatch("loading/load", "update_patient", { root: true })
		return session
		.patch(`/api/patients/${state.current?.id}/status/`, {blocked: status})
		.then((demographics) => {
			commit("set_current_patient", demographics.data)
			return state.current
		})
    .finally(() => {
      dispatch("loading/end", "update_patient", { root: true })
    })
	}
}

// mutations
const mutations = {
  reset_state(state) {
		Object.assign(state, initialState())
  },

	set_plugins(state, plugins) {
		state.plugins = plugins
	},
	set_physicians(state, physicians) {
		state.physicians = physicians
	},
	set_current_patient(state, current) {
		state.current = current
	},
	set_current_data(state, { key, value }) {
		Vue.set(state, key, value)
	}
}

export default {
	namespaced: true,
	state,
	getters,
	actions,
	mutations,
}
